import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Settings, Plus, UserCog, Users } from "lucide-react";
import { toast } from "sonner";
import { professionalLabel } from "@/lib/professional";

export default function AdminHome() {
  const [pros, setPros] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Compasso@2025", role: "patient", professional_id: "", professional_type: "counselor" });

  const load = async () => {
    const [p, pt] = await Promise.all([api.get("/users?role=professional"), api.get("/patients")]);
    setPros(p.data); setPatients(pt.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email) { toast.error("Preencha nome e e-mail."); return; }
    try {
      await api.post("/admin/users", {
        ...form, professional_id: form.role === "patient" ? (form.professional_id || null) : null,
      });
      toast.success("Usuário criado."); setOpen(false);
      setForm({ name: "", email: "", password: "Compasso@2025", role: "patient", professional_id: "", professional_type: "counselor" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const proName = Object.fromEntries(pros.map((p) => [p.id, p.name]));

  return (
    <div className="max-w-4xl mx-auto space-y-5 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F3B4D] flex items-center gap-2"><Settings className="text-[#0F766E]" /> Organização</h1>
          <p className="text-sm text-[#94A3B8]">Gerencie profissionais e pacientes da Clínica Compasso.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="admin-add-user-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Plus size={16} /> Novo usuário</Button>
      </div>

      <Tabs defaultValue="patients">
        <TabsList className="rounded-full bg-[#E0F2FE]/60">
          <TabsTrigger value="patients" data-testid="admin-tab-patients" className="rounded-full data-[state=active]:bg-[#0F766E] data-[state=active]:text-white gap-1.5"><Users size={14} /> Pacientes</TabsTrigger>
          <TabsTrigger value="pros" data-testid="admin-tab-pros" className="rounded-full data-[state=active]:bg-[#0F766E] data-[state=active]:text-white gap-1.5"><UserCog size={14} /> Profissionais</TabsTrigger>
        </TabsList>
        <TabsContent value="patients" className="mt-4 space-y-2">
          {patients.map((p) => (
            <Card key={p.id} className="rounded-2xl p-4 border-[#E0F2FE] flex items-center justify-between" data-testid={`admin-patient-${p.id}`}>
              <div><div className="font-semibold text-[#1F3B4D]">{p.name}</div><div className="text-xs text-[#94A3B8]">{p.email}</div></div>
              <Badge variant="secondary">{proName[p.professional_id] || "Sem profissional"}</Badge>
            </Card>
          ))}
          {patients.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum paciente.</p>}
        </TabsContent>
        <TabsContent value="pros" className="mt-4 space-y-2">
          {pros.map((p) => (
            <Card key={p.id} className="rounded-2xl p-4 border-[#E0F2FE] flex items-center justify-between" data-testid={`admin-pro-${p.id}`}>
              <div><div className="font-semibold text-[#1F3B4D]">{p.name}</div><div className="text-xs text-[#94A3B8]">{p.email}</div></div>
              <Badge className="bg-[#0F766E]/10 text-[#0F766E] border-0" data-testid={`admin-pro-type-${p.id}`}>{professionalLabel(p.professional_type)}</Badge>
            </Card>
          ))}
          {pros.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum profissional.</p>}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">Novo usuário</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" className="rounded-xl border-[#E0F2FE]" /></div>
            <div className="space-y-1"><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" className="rounded-xl border-[#E0F2FE]" /></div>
            <div className="space-y-1"><Label>Senha inicial</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" className="rounded-xl border-[#E0F2FE]" /></div>
            <div className="space-y-1"><Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-xl border-[#E0F2FE]" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="patient">Paciente</SelectItem><SelectItem value="professional">Profissional</SelectItem></SelectContent>
              </Select>
            </div>
            {form.role === "professional" && (
              <div className="space-y-1"><Label>Tipo de profissional</Label>
                <Select value={form.professional_type} onValueChange={(v) => setForm({ ...form, professional_type: v })}>
                  <SelectTrigger className="rounded-xl border-[#E0F2FE]" data-testid="admin-pro-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="counselor">Conselheiro(a)</SelectItem><SelectItem value="psychologist">Psicólogo(a)</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            {form.role === "patient" && (
              <div className="space-y-1"><Label>Profissional responsável</Label>
                <Select value={form.professional_id} onValueChange={(v) => setForm({ ...form, professional_id: v })}>
                  <SelectTrigger className="rounded-xl border-[#E0F2FE]" data-testid="admin-assign-pro-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{pros.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} data-testid="admin-create-user-submit" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259]">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
